import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "./cn";

/**
 * Botões do design system Renke × Workflow.
 * - primario: amarelo, a ação principal da região (uma por card/tela);
 * - tinta: ação forte alternativa (preto no claro, claro no escuro);
 * - secundario/contorno: outline sobre card;
 * - fantasma: ação discreta; perigo só em confirmação destrutiva.
 */
export const variantesDoBotao = cva(
  [
    "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap",
    "rounded-md border border-transparent text-sm font-semibold tracking-[-0.005em]",
    "transition-[background-color,box-shadow,border-color,color,opacity,transform] duration-150 ease-out active:scale-[0.98]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:stroke-[1.75]",
  ],
  {
    variants: {
      variante: {
        primario:
          "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary-pressed",
        tinta: "bg-ink text-ink-foreground hover:bg-ink-hover",
        secundario:
          "border-border bg-card text-foreground hover:border-border-strong hover:bg-sunken",
        contorno:
          "border-border bg-card text-foreground hover:border-border-strong hover:bg-sunken",
        fantasma:
          "text-foreground-secondary hover:bg-sunken hover:text-foreground",
        perigo:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        "perigo-contorno":
          "border-danger-line bg-transparent text-danger-soft-foreground hover:bg-danger-soft",
        elo: "h-auto px-0 text-foreground underline decoration-[1.5px] underline-offset-[3px] hover:decoration-primary",
      },
      tamanho: {
        pequeno: "h-8 rounded-sm px-3 text-[13px]",
        medio: "h-10 px-4",
        grande: "h-11 px-5 text-[15px]",
        icone: "size-10",
      },
    },
    defaultVariants: {
      variante: "primario",
      tamanho: "medio",
    },
  },
);

export type VarianteDeBotao = NonNullable<
  VariantProps<typeof variantesDoBotao>["variante"]
>;
export type TamanhoDeBotao = NonNullable<
  VariantProps<typeof variantesDoBotao>["tamanho"]
>;

export type PropsDoBotao = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "className"
> &
  VariantProps<typeof variantesDoBotao> & {
    readonly carregando?: boolean | undefined;
    readonly asChild?: boolean | undefined;
    readonly children: ReactNode;
    readonly className?: string | undefined;
  };

export function Botao({
  variante,
  tamanho,
  carregando = false,
  asChild = false,
  disabled,
  children,
  className,
  type = "button",
  ...resto
}: PropsDoBotao) {
  const classes = cn(variantesDoBotao({ variante, tamanho }), className);
  const desabilitado = disabled === true || carregando;

  // Slot exige exatamente um filho. Spinner + children quebra o modal com
  // "React.Children.only" e deixa a tela em branco.
  if (asChild) {
    return (
      <Slot
        {...resto}
        aria-busy={carregando}
        className={classes}
      >
        {children}
      </Slot>
    );
  }

  return (
    <button
      {...resto}
      type={type}
      disabled={desabilitado}
      aria-busy={carregando}
      className={classes}
    >
      {carregando ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}
